export interface ValidationResult {
  readonly valid: boolean;
  readonly error?: string;
  readonly details?: Record<string, unknown>;
}

export const ALL_SINS = [
  "greed",
  "gluttony",
  "envy",
  "lust",
  "pride",
  "wrath",
  "sloth",
] as const;
export type SinType = (typeof ALL_SINS)[number];

export const BLACK_DIE_SHAPES = [
  "d4",
  "d6",
  "d8",
  "d10",
  "d12",
  "d20",
] as const;
export type BlackDieShape = (typeof BLACK_DIE_SHAPES)[number];
export type DieId = BlackDieShape | "white";

export const DIE_FACES: Readonly<Record<BlackDieShape, number>> = {
  d4: 4,
  d6: 6,
  d8: 8,
  d10: 10,
  d12: 12,
  d20: 20,
};

/** Polar position on the shared virtual circular table. radius in [0, 1). */
export interface DiePosition {
  readonly angle: number;
  readonly radius: number;
}

export interface LockedDie {
  readonly ownerId: string;
  readonly sin: SinType;
}

export interface Die {
  readonly id: DieId;
  readonly color: "black" | "white";
  /** Rolled value. d10's 0 face is stored as 10. */
  readonly value: number;
  readonly position: DiePosition;
  readonly lockedBy: LockedDie | null;
}

export interface CarnivalRngState {
  readonly algorithm: "mulberry32";
  readonly state: number;
}

export interface CarnivalPlayer {
  readonly playerId: string;
  readonly name: string;
}

export type CarnivalPhase = "SECRET_SELECT" | "REVEAL_RESOLVE" | "GAME_OVER";

export interface RevealedSinRecord {
  readonly playerId: string;
  readonly sin: SinType;
  /** Reveal order position for this round (0-indexed), for Pride tie-break / devil handoff. */
  readonly revealIndex: number;
}

export interface RoundSummary {
  readonly round: number;
  /** Raw round score per player, before the white die bonus. */
  readonly rawScoreByPlayerId: Readonly<Record<string, number>>;
  /** Final round score per player, after the white die bonus. */
  readonly finalScoreByPlayerId: Readonly<Record<string, number>>;
  readonly whiteDieValue: number;
  readonly lowestScorePlayerIds: readonly string[];
  readonly prideRevealerIds: readonly string[];
  readonly nextDevilCardHolderId: string;
}

export const TOTAL_ROUNDS = 7;

export interface CarnivalGameState {
  readonly gameId: string;
  readonly gameSeed: string;
  readonly rngState: CarnivalRngState;
  readonly phase: CarnivalPhase;
  readonly round: number;
  readonly players: readonly CarnivalPlayer[];
  /** Fixed clockwise seating order, set at game start. */
  readonly seatingOrder: readonly string[];
  readonly devilCardHolderId: string;
  /** This round's reveal order: devil holder first, then clockwise. */
  readonly revealOrder: readonly string[];
  readonly nextToRevealIndex: number;
  readonly dice: readonly Die[];
  readonly handsByPlayerId: Readonly<Record<string, readonly SinType[]>>;
  /** Hidden: this round's secret pick, per player. Redacted in viewer projection. */
  readonly selectionsByPlayerId: Readonly<Record<string, SinType | null>>;
  readonly revealedThisRound: readonly RevealedSinRecord[];
  readonly scoreHistoryByPlayerId: Readonly<Record<string, readonly number[]>>;
  readonly lastRoundSummary: RoundSummary | null;
  readonly winnerIds: readonly string[] | null;
}

export interface SelectSinAction {
  readonly type: "SelectSin";
  readonly playerId: string;
  readonly sin: SinType;
}

export interface LustPick {
  readonly dieId: DieId;
  readonly stolenFromPlayerId?: string;
}

export interface WrathTarget {
  readonly dieId: DieId;
  readonly fromPlayerId?: string;
}

/**
 * Greed/Gluttony/Lust require a matching target on the table; the rulebook
 * has each fall back to "take none" when no valid target exists, so their
 * payload is nullable. Wrath always has a valid target (all 6 black dice are
 * always in play, somewhere) and Pride/Envy/Sloth take no player choice at
 * all — Sloth's nearest-die pick is computed automatically, not chosen.
 */
export type SinChoice =
  | { readonly sin: "greed"; readonly dieIds: readonly [DieId, DieId] | null }
  | { readonly sin: "gluttony"; readonly dieId: DieId | null }
  | { readonly sin: "envy" }
  | {
      readonly sin: "lust";
      readonly picks: readonly [LustPick, LustPick] | null;
    }
  | { readonly sin: "pride" }
  | { readonly sin: "wrath"; readonly target: WrathTarget }
  | { readonly sin: "sloth" };

export interface RevealSinAction {
  readonly type: "RevealSin";
  readonly playerId: string;
  readonly choice: SinChoice;
}

export type CarnivalAction = SelectSinAction | RevealSinAction;

export interface CarnivalViewerState {
  readonly gameId: string;
  readonly phase: CarnivalPhase;
  readonly round: number;
  readonly players: readonly CarnivalPlayer[];
  readonly seatingOrder: readonly string[];
  readonly devilCardHolderId: string;
  readonly revealOrder: readonly string[];
  readonly nextToRevealIndex: number;
  readonly dice: readonly Die[];
  readonly handCountsByPlayerId: Readonly<Record<string, number>>;
  /** Full hand for the requesting player; empty for observers / other players. */
  readonly ownHand: readonly SinType[];
  /** This viewer's own pending selection this round, if any; null for observers / other players. */
  readonly ownPendingSelection: SinType | null;
  /** Who has locked in a selection this round (not what they picked). */
  readonly playersWhoSelectedThisRound: readonly string[];
  readonly revealedThisRound: readonly RevealedSinRecord[];
  readonly scoreHistoryByPlayerId: Readonly<Record<string, readonly number[]>>;
  readonly lastRoundSummary: RoundSummary | null;
  readonly winnerIds: readonly string[] | null;
}
